import type {
  AbsoluteBounds,
  DiagnosticSink,
  GroupTransform,
  Hyperlink,
  PlaceholderRef,
  PlaceholderType,
  Shape,
  TableCell,
  TableRow,
  Transform,
} from '@slideforge/shared';
import { IDENTITY_TRANSFORM, applyGroupTransform, transformToBounds } from '@slideforge/shared';
import type { Element } from '../xml.js';
import type { PropertyResolver } from '../property-resolver.js';
import { parseEffects, parseFill, parseLine } from './dml.js';
import { parseGeometry, parseGroupTransform, parseTransform } from './geometry.js';
import { parseHyperlink, parseTextBody, type TextContext } from './text.js';
import type { MediaPlayback } from './timing.js';

export interface ShapeContext extends TextContext {
  readonly diagnostics: DiagnosticSink;
  readonly slideIndex?: number;
  readonly partName: string;
  /** Only present for slide shapes; layout and master shapes inherit nothing. */
  readonly resolver?: PropertyResolver;
  /** Maps a `p:sldId`-style relationship to a slide id, for hyperlink jumps. */
  readonly mediaIdForPart?: (partName: string) => string | undefined;
  /** Playback settings from `p:timing`, keyed by shape id. See `parseMediaPlayback`. */
  readonly mediaPlaybackFor?: (shapeId: string) => MediaPlayback | undefined;
}

export function parseShapeTree(
  spTree: Element | undefined,
  ctx: ShapeContext,
  parent?: GroupTransform,
): Shape[] {
  if (!spTree) return [];
  const out: Shape[] = [];
  for (const el of spTree.children) {
    const shape = ctx.diagnostics.guard(
      'shape.parse-failed',
      { slideIndex: ctx.slideIndex, partName: ctx.partName, shapeName: nameOf(el) },
      undefined,
      () => parseShape(el, ctx, parent),
    );
    if (shape) out.push(shape);
  }
  return out;
}

function parseShape(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape | undefined {
  switch (el.name) {
    case 'p:sp':
      return parseAutoShape(el, ctx, parent);
    case 'p:pic':
      return parsePicture(el, ctx, parent);
    case 'p:cxnSp':
      return parseConnector(el, ctx, parent);
    case 'p:grpSp':
      return parseGroup(el, ctx, parent);
    case 'p:graphicFrame':
      return parseGraphicFrame(el, ctx, parent);
    case 'p:contentPart':
      ctx.diagnostics.report(
        'warning',
        'shape.content-part',
        'An ink or external content part was skipped; it has no HTML equivalent.',
        { slideIndex: ctx.slideIndex, partName: ctx.partName },
      );
      return undefined;
    default:
      return undefined;
  }
}

interface Identity {
  id: string;
  name: string;
  hidden: boolean;
  altText?: string;
  title?: string;
}

function identityOf(nvPr: Element | undefined, kind: string): Identity {
  const cNv = nvPr?.child('p:cNvPr');
  const descr = cNv?.attr('descr');
  const title = cNv?.attr('title');
  return {
    id: cNv?.attr('id') ?? `${kind}-unknown`,
    name: cNv?.attr('name') ?? kind,
    hidden: cNv?.bool('hidden', false) ?? false,
    ...(descr ? { altText: descr } : {}),
    ...(title ? { title } : {}),
  };
}

function nameOf(el: Element): string {
  return el.find((e) => e.name === 'p:cNvPr')?.attr('name') ?? el.name;
}

function placeholderOf(nvPr: Element | undefined): PlaceholderRef | undefined {
  const ph = nvPr?.child('p:nvPr')?.child('p:ph');
  if (!ph) return undefined;
  const idxRaw = ph.attr('idx');
  const size = ph.attr('sz');
  return {
    type: (ph.attr('type') as PlaceholderType | undefined) ?? 'obj',
    ...(idxRaw !== undefined ? { idx: Number.parseInt(idxRaw, 10) } : {}),
    ...(size ? { size: size as PlaceholderRef['size'] } : {}),
    ...(ph.attr('hasCustomPrompt') ? { hasCustomPrompt: ph.bool('hasCustomPrompt', false) } : {}),
  };
}

function resolvePlacement(
  own: Transform | undefined,
  placeholder: PlaceholderRef | undefined,
  ctx: ShapeContext,
  parent: GroupTransform | undefined,
): { transform: Transform; bounds: AbsoluteBounds; inherited: boolean } {
  let transform = own ?? IDENTITY_TRANSFORM;
  let inherited = false;

  if (placeholder && ctx.resolver) {
    const resolved = ctx.resolver.resolveTransform(placeholder, own);
    if (resolved.transform) {
      transform = resolved.transform;
      inherited = resolved.inherited;
    }
  }

  const bounds = parent ? applyGroupTransform(transform, parent) : transformToBounds(transform);
  return { transform, bounds, inherited };
}

function parseAutoShape(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape {
  const nvSpPr = el.child('p:nvSpPr');
  const identity = identityOf(nvSpPr, 'Shape');
  const placeholder = placeholderOf(nvSpPr);
  const spPr = el.child('p:spPr');
  const { transform, bounds, inherited } = resolvePlacement(
    parseTransform(spPr?.child('a:xfrm')),
    placeholder,
    ctx,
    parent,
  );

  const link = parseHyperlink(nvSpPr?.child('p:cNvPr'), ctx);

  return {
    kind: 'shape',
    ...identity,
    transform,
    bounds,
    inheritedTransform: inherited,
    ...(placeholder ? { placeholder } : {}),
    ...(link ? { hyperlink: link } : {}),
    geometry: parseGeometry(spPr),
    fill: parseFill(spPr, ctx),
    ...(parseLine(spPr?.child('a:ln'), ctx) ? { line: parseLine(spPr?.child('a:ln'), ctx) } : {}),
    effects: parseEffects(spPr?.child('a:effectLst')),
    ...(parseTextBody(el.child('p:txBody'), ctx)
      ? { text: parseTextBody(el.child('p:txBody'), ctx) }
      : {}),
  } as Shape;
}

function parsePicture(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape | undefined {
  const nvPicPr = el.child('p:nvPicPr');
  const identity = identityOf(nvPicPr, 'Picture');
  const placeholder = placeholderOf(nvPicPr);
  const spPr = el.child('p:spPr');
  const { transform, bounds, inherited } = resolvePlacement(
    parseTransform(spPr?.child('a:xfrm')),
    placeholder,
    ctx,
    parent,
  );

  const blipFill = el.child('p:blipFill');
  const blip = blipFill?.child('a:blip');
  const embed = blip?.attr('r:embed');
  const link = blip?.attr('r:link');
  const mediaId = embed ? ctx.mediaIdFor(embed) : undefined;
  const hyperlink = parseHyperlink(nvPicPr?.child('p:cNvPr'), ctx);

  // An audio or video clip is also a `p:pic`: the shape tree holds its poster
  // frame, and `p:nvPr` says which relationship carries the actual clip. Read
  // that before falling through, or the deck publishes with a still image where
  // the author put a video.
  const source = mediaSourceOf(nvPicPr?.child('p:nvPr'), ctx);
  if (source) {
    const playback = ctx.mediaPlaybackFor?.(identity.id);
    const clipId = source.relIds
      .map((relId) => ctx.mediaIdFor(relId))
      .find((id): id is string => id !== undefined);

    if (!clipId) {
      // Linked rather than embedded: the bytes live outside the package, so the
      // course would ship a player pointing at the author's hard disk.
      const unresolved = source.relIds[0];
      if (unresolved) ctx.onLinkedMedia?.(unresolved);
    }

    return {
      kind: 'media',
      ...identity,
      transform,
      bounds,
      inheritedTransform: inherited,
      ...(placeholder ? { placeholder } : {}),
      ...(hyperlink ? { hyperlink } : {}),
      mediaKind: source.mediaKind,
      ...(clipId ? { mediaId: clipId } : {}),
      ...(mediaId ? { posterMediaId: mediaId } : {}),
      ...(!clipId && source.relIds[0] ? { linkedTarget: source.relIds[0] } : {}),
      autoPlay: playback?.autoPlay ?? false,
      loop: playback?.loop ?? false,
      ...(playback && playback.volume < 1 ? { volume: playback.volume } : {}),
      ...(playback?.muted ? { muted: true } : {}),
    } as Shape;
  }

  if (!embed && link) {
    ctx.onLinkedMedia?.(link);
  }

  const srcRect = blipFill?.child('a:srcRect');
  const crop = {
    cropLeft: (srcRect?.int('l', 0) ?? 0) / 100000,
    cropTop: (srcRect?.int('t', 0) ?? 0) / 100000,
    cropRight: (srcRect?.int('r', 0) ?? 0) / 100000,
    cropBottom: (srcRect?.int('b', 0) ?? 0) / 100000,
  };

  return {
    kind: 'picture',
    ...identity,
    transform,
    bounds,
    inheritedTransform: inherited,
    ...(placeholder ? { placeholder } : {}),
    ...(hyperlink ? { hyperlink } : {}),
    mediaId: mediaId ?? '',
    ...(link && !embed ? { linkedTarget: link } : {}),
    geometry: parseGeometry(spPr),
    fill: parseFill(spPr, ctx),
    ...(parseLine(spPr?.child('a:ln'), ctx) ? { line: parseLine(spPr?.child('a:ln'), ctx) } : {}),
    effects: parseEffects(spPr?.child('a:effectLst')),
    ...crop,
  } as Shape;
}

/**
 * The media reference inside a picture's `p:nvPr`, if it is really a clip.
 *
 * ECMA-376 spells the reference `a:videoFile`/`a:audioFile` with `r:link`, even
 * for a clip embedded in the package — the attribute name is a historical
 * artefact, not a statement that the file is external. PowerPoint 2010 and
 * later additionally writes a `p14:media` extension with `r:embed`, pointing at
 * the same part through a different relationship type. Both are collected and
 * tried in turn, because decks in the wild carry one, the other, or both.
 */
interface MediaSource {
  readonly mediaKind: 'audio' | 'video';
  /** Candidate relationship ids, most trustworthy first. */
  readonly relIds: readonly string[];
}

const MEDIA_ELEMENTS = new Map<string, 'audio' | 'video'>([
  ['a:videoFile', 'video'],
  ['a:audioFile', 'audio'],
  ['a:wavAudioFile', 'audio'],
]);

const P14_MEDIA_EXT_URI = '{DAA4B4D4-6D71-4841-9C94-3DE7FCFB9230}';

function mediaSourceOf(nvPr: Element | undefined, ctx: ShapeContext): MediaSource | undefined {
  if (!nvPr) return undefined;

  if (nvPr.has('p:audioCd')) {
    ctx.diagnostics.report(
      'warning',
      'media.audio-cd',
      'An audio CD track was skipped: the audio is not in the file, so it cannot be published.',
      { slideIndex: ctx.slideIndex, partName: ctx.partName },
    );
    return undefined;
  }

  const el = nvPr.children.find((c) => MEDIA_ELEMENTS.has(c.name));
  const mediaKind = el ? MEDIA_ELEMENTS.get(el.name) : undefined;
  if (!el || !mediaKind) return undefined;

  const relIds: string[] = [];
  const embedded = nvPr
    .child('p:extLst')
    ?.all('p:ext')
    .find((ext) => ext.attr('uri') === P14_MEDIA_EXT_URI)
    ?.child('p14:media')
    ?.attr('r:embed');
  if (embedded) relIds.push(embedded);

  for (const attr of ['r:embed', 'r:link'] as const) {
    const relId = el.attr(attr);
    if (relId && !relIds.includes(relId)) relIds.push(relId);
  }

  return relIds.length > 0 ? { mediaKind, relIds } : undefined;
}

function parseConnector(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape {
  const nvCxnSpPr = el.child('p:nvCxnSpPr');
  const identity = identityOf(nvCxnSpPr, 'Connector');
  const spPr = el.child('p:spPr');
  const { transform, bounds, inherited } = resolvePlacement(
    parseTransform(spPr?.child('a:xfrm')),
    undefined,
    ctx,
    parent,
  );
  const cNvCxnSpPr = nvCxnSpPr?.child('p:cNvCxnSpPr');
  const start = cNvCxnSpPr?.child('a:stCxn')?.attr('id');
  const end = cNvCxnSpPr?.child('a:endCxn')?.attr('id');

  return {
    kind: 'connector',
    ...identity,
    transform,
    bounds,
    inheritedTransform: inherited,
    geometry: parseGeometry(spPr),
    ...(parseLine(spPr?.child('a:ln'), ctx) ? { line: parseLine(spPr?.child('a:ln'), ctx) } : {}),
    ...(start ? { startShapeId: start } : {}),
    ...(end ? { endShapeId: end } : {}),
  } as Shape;
}

function parseGroup(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape {
  const nvGrpSpPr = el.child('p:nvGrpSpPr');
  const identity = identityOf(nvGrpSpPr, 'Group');
  const grpSpPr = el.child('p:grpSpPr');
  const own = parseGroupTransform(grpSpPr?.child('a:xfrm'));

  // The group's own box is placed in its parent's space; its children are then
  // placed inside that box using the group's child coordinate system.
  const bounds = parent ? applyGroupTransform(own, parent) : transformToBounds(own);
  const placed: GroupTransform = {
    ...bounds,
    chX: own.chX,
    chY: own.chY,
    chCx: own.chCx,
    chCy: own.chCy,
  };

  return {
    kind: 'group',
    ...identity,
    transform: own,
    bounds,
    inheritedTransform: false,
    groupTransform: placed,
    fill: parseFill(grpSpPr, ctx),
    children: parseShapeTree(el, ctx, placed),
  } as Shape;
}

const GRAPHIC_URIS: Readonly<Record<string, 'chart' | 'smartArt' | 'ole' | 'model3d' | 'table'>> = {
  'http://schemas.openxmlformats.org/drawingml/2006/table': 'table',
  'http://schemas.openxmlformats.org/drawingml/2006/chart': 'chart',
  'http://schemas.openxmlformats.org/drawingml/2006/diagram': 'smartArt',
  'http://schemas.openxmlformats.org/presentationml/2006/ole': 'ole',
  'http://schemas.microsoft.com/office/drawing/2014/model3d': 'model3d',
};

function parseGraphicFrame(el: Element, ctx: ShapeContext, parent?: GroupTransform): Shape {
  const nvGraphicFramePr = el.child('p:nvGraphicFramePr');
  const identity = identityOf(nvGraphicFramePr, 'Graphic');
  const placeholder = placeholderOf(nvGraphicFramePr);
  const { transform, bounds, inherited } = resolvePlacement(
    parseTransform(el.child('p:xfrm')),
    placeholder,
    ctx,
    parent,
  );

  const graphicData = el.path('a:graphic', 'a:graphicData');
  const uri = graphicData?.attr('uri') ?? '';
  const kind = GRAPHIC_URIS[uri] ?? 'unknown';

  if (kind === 'table') {
    const tbl = graphicData?.child('a:tbl');
    return {
      kind: 'table',
      ...identity,
      transform,
      bounds,
      inheritedTransform: inherited,
      ...(placeholder ? { placeholder } : {}),
      ...parseTable(tbl, ctx),
    } as Shape;
  }

  ctx.diagnostics.report(
    'warning',
    `graphicFrame.${kind}`,
    kind === 'unknown'
      ? 'An unrecognised embedded object was kept as a static image where one was available.'
      : `A ${kind} is rendered from its fallback image rather than reconstructed.`,
    { slideIndex: ctx.slideIndex, shapeName: identity.name, partName: ctx.partName },
  );

  const fallback = findFallbackImage(el, ctx);

  return {
    kind: 'graphicFrame',
    ...identity,
    transform,
    bounds,
    inheritedTransform: inherited,
    ...(placeholder ? { placeholder } : {}),
    frameKind: kind,
    ...(fallback ? { fallbackMediaId: fallback } : {}),
  } as Shape;
}

/**
 * Charts, SmartArt and OLE objects usually ship a rendered fallback image in a
 * `mc:AlternateContent` block or as a related media part. Using it is the
 * difference between a Hybrid slide and a hole.
 */
function findFallbackImage(el: Element, ctx: ShapeContext): string | undefined {
  const blip = el.find((e) => e.name === 'a:blip');
  const embed = blip?.attr('r:embed');
  if (embed) return ctx.mediaIdFor(embed);
  return undefined;
}

function parseTable(
  tbl: Element | undefined,
  ctx: ShapeContext,
): {
  columnWidths: number[];
  rows: TableRow[];
  firstRow: boolean;
  firstColumn: boolean;
  bandRow: boolean;
  bandColumn: boolean;
  styleId?: string;
} {
  const tblPr = tbl?.child('a:tblPr');
  const styleId = tblPr?.child('a:tableStyleId')?.innerText();
  const columnWidths = (tbl?.path('a:tblGrid')?.all('a:gridCol') ?? []).map((c) => c.int('w', 0));

  const rows: TableRow[] = (tbl?.all('a:tr') ?? []).map((tr) => ({
    height: tr.int('h', 0),
    cells: tr.all('a:tc').map((tc) => parseCell(tc, ctx)),
  }));

  return {
    columnWidths,
    rows,
    firstRow: tblPr?.bool('firstRow', false) ?? false,
    firstColumn: tblPr?.bool('firstCol', false) ?? false,
    bandRow: tblPr?.bool('bandRow', false) ?? false,
    bandColumn: tblPr?.bool('bandCol', false) ?? false,
    ...(styleId ? { styleId } : {}),
  };
}

function parseCell(tc: Element, ctx: ShapeContext): TableCell {
  const tcPr = tc.child('a:tcPr');
  const anchor = tcPr?.attr('anchor');
  const text = parseTextBody(tc.child('a:txBody'), ctx);
  return {
    text: text ?? { bodyProps: parseTextBody(undefined, ctx)?.bodyProps ?? EMPTY_BODY, paragraphs: [] },
    gridSpan: tc.int('gridSpan', 1),
    rowSpan: tc.int('rowSpan', 1),
    hMerge: tc.bool('hMerge', false),
    vMerge: tc.bool('vMerge', false),
    fill: parseFill(tcPr, ctx),
    ...(parseLine(tcPr?.child('a:lnL'), ctx) ? { borderLeft: parseLine(tcPr?.child('a:lnL'), ctx) } : {}),
    ...(parseLine(tcPr?.child('a:lnR'), ctx) ? { borderRight: parseLine(tcPr?.child('a:lnR'), ctx) } : {}),
    ...(parseLine(tcPr?.child('a:lnT'), ctx) ? { borderTop: parseLine(tcPr?.child('a:lnT'), ctx) } : {}),
    ...(parseLine(tcPr?.child('a:lnB'), ctx) ? { borderBottom: parseLine(tcPr?.child('a:lnB'), ctx) } : {}),
    marginLeft: tcPr?.int('marL', 91440) ?? 91440,
    marginRight: tcPr?.int('marR', 91440) ?? 91440,
    marginTop: tcPr?.int('marT', 45720) ?? 45720,
    marginBottom: tcPr?.int('marB', 45720) ?? 45720,
    anchor: anchor === 'ctr' ? 'ctr' : anchor === 'b' ? 'b' : 't',
  };
}

const EMPTY_BODY = {
  anchor: 't' as const,
  anchorCenter: false,
  wrap: 'square' as const,
  insetLeft: 91440,
  insetTop: 45720,
  insetRight: 91440,
  insetBottom: 45720,
  autofit: { kind: 'none' as const },
  columnCount: 1,
  columnSpacing: 0,
  vertical: 'horz' as const,
};

export type { Hyperlink };
